/**
 * 確定済会計期の境界取得と、過年度 action の保存先振り分けに使う helper を提供する。
 * 通常 action とロック期間中取引の insert row を同じ action 配列から構築できるようにする。
 */
import { type Action as CoreAction, actionTypes } from '@0xtorch/core'
import type { Action as BasicAction } from '@pkg/basic'
import { divideArrayIntoChunks, hexToBlob } from '@pkg/basic'
import type { Hex } from '@pkg/evm'
import { eq, inArray, sql } from 'drizzle-orm'
import type { SQLiteInsertValue } from 'drizzle-orm/sqlite-core'
import {
  actionAccountRelationColumnCount,
  actionColumnCount,
  actionTransferColumnCount,
  cryptoCurrencyColumnCount,
  nftColumnCount,
  sqliteMaxCompoundSelectCount,
  sqliteMaxHostParameterCount,
} from '../../constants'
import { actionsToInsertSqls } from '../../createSql/actionsToInsertSqls'
import {
  accountingPeriodTable,
  actionAccountRelationTable,
  type actionSourceTable,
  actionTable,
  actionTransferTable,
  cryptoCurrencyTable,
  lockedPeriodActionAccountRelationTable,
  lockedPeriodActionTable,
  lockedPeriodActionTransferTable,
  nftTable,
  portfolioTable,
} from '../../schema'
import type { DatabaseWithTransaction, TransactionDatabase } from '../../types'
import { getMaxInsertRowCount } from '../../utils'
import { applyActionEditRulesToInsertedActionsOnDatabase } from './applyActionEditRulesToInsertedActions'
import { deleteAssistantProposalsForActionIds } from './deleteAssistantProposalsForActionKeys'

type ActionLike = BasicAction | CoreAction

type ActionAccountRelationRow = {
  readonly actionId: string
  readonly accountId: number
}

type UpdatedActionTransferMutableRow = {
  readonly cryptoId: string | null
  readonly fiatId: string | null
  readonly nftId: string | null
  readonly amount: string
  readonly price: string | null
}

type UpdatedActionTransferMutableRowObject = {
  readonly [key: string]: unknown
  readonly cryptoId?: unknown
  readonly cryptoid?: unknown
  readonly fiatId?: unknown
  readonly fiatid?: unknown
  readonly nftId?: unknown
  readonly nftid?: unknown
  readonly amount?: unknown
  readonly price?: unknown
}

export type ClosedAccountingPeriodBoundary = {
  readonly accountingPeriodId: number
  readonly endTimestamp: number
}

type LockedPeriodActionRow = typeof lockedPeriodActionTable.$inferSelect
type LockedPeriodActionTransferRow =
  typeof lockedPeriodActionTransferTable.$inferSelect
type LockedPeriodActionAccountRelationRow =
  typeof lockedPeriodActionAccountRelationTable.$inferSelect

export type PartitionedActions<TAction extends { readonly timestamp: number }> =
  {
    readonly openActions: readonly TAction[]
    readonly lockedActions: readonly TAction[]
  }

export type PartitionedSourceIds = {
  readonly openSourceIds: readonly string[]
  readonly lockedSourceIds: readonly string[]
  readonly unclassifiedSourceIds: readonly string[]
}

export type LockedPeriodActionInsertRows = {
  readonly actionRows: readonly SQLiteInsertValue<
    typeof lockedPeriodActionTable
  >[]
  readonly actionTransferRows: readonly SQLiteInsertValue<
    typeof lockedPeriodActionTransferTable
  >[]
  readonly actionAccountRelationRows: readonly SQLiteInsertValue<
    typeof lockedPeriodActionAccountRelationTable
  >[]
  readonly cryptoRows: readonly SQLiteInsertValue<typeof cryptoCurrencyTable>[]
  readonly nftRows: readonly SQLiteInsertValue<typeof nftTable>[]
}

export type ActionInsertRows = {
  readonly actionRows: readonly SQLiteInsertValue<typeof actionTable>[]
  readonly actionTransferRows: readonly SQLiteInsertValue<
    typeof actionTransferTable
  >[]
  readonly cryptoRows: readonly SQLiteInsertValue<typeof cryptoCurrencyTable>[]
  readonly nftRows: readonly SQLiteInsertValue<typeof nftTable>[]
}

export type PreparedFiscalLockInsertRows<
  TAction extends { readonly timestamp: number },
> = {
  readonly openActions: readonly TAction[]
  readonly lockedActions: readonly TAction[]
  readonly openActionIds: readonly string[]
  readonly openInsertRows: ActionInsertRows
  readonly lockedInsertRows: LockedPeriodActionInsertRows
}

type ActionRow = typeof actionTable.$inferSelect
type ActionTransferRow = typeof actionTransferTable.$inferSelect

const actionEvidenceValues = [
  'contract',
  'system-rule',
  'user-rule',
  'manual-check',
  'assistant-check',
  'none',
] as const

const actionCrossTypeValues = ['start', 'middle', 'end'] as const

type UpdatedActionMutableRow = {
  readonly type: ActionRow['type']
  readonly subType: ActionRow['subType']
  readonly evidence: ActionRow['evidence']
  readonly comment: ActionRow['comment']
  readonly loanId: ActionRow['loanId']
  readonly crossId: ActionRow['crossId']
  readonly crossType: ActionRow['crossType']
  readonly locked: ActionRow['locked']
  readonly generatedTx: ActionRow['generatedTx']
  readonly relatedAssetIds: ActionRow['relatedAssetIds']
  readonly fromAddresses: ActionRow['fromAddresses']
  readonly toAddresses: ActionRow['toAddresses']
}

type UpdatedActionMutableRowObject = {
  readonly [key: string]: unknown
  readonly type?: unknown
  readonly subType?: unknown
  readonly evidence?: unknown
  readonly comment?: unknown
  readonly loanId?: unknown
  readonly crossId?: unknown
  readonly crossType?: unknown
  readonly locked?: unknown
  readonly generatedTx?: unknown
  readonly relatedAssetIds?: unknown
  readonly fromAddresses?: unknown
  readonly toAddresses?: unknown
}

type LockedActionInvariantValues = {
  readonly totalPl: ActionRow['totalPl']
  readonly totalPlSortKey: ActionRow['totalPlSortKey']
  readonly app: ActionRow['app']
  readonly targetId: ActionRow['targetId']
  readonly hasUnknownPriceTx: ActionRow['hasUnknownPriceTx']
  readonly hasInsufficientAmountTx: ActionRow['hasInsufficientAmountTx']
  readonly csvId: ActionRow['csvId']
  readonly apiId: ActionRow['apiId']
}

const isActionTypeValue = (value: unknown): value is ActionRow['type'] => {
  return (
    typeof value === 'string' &&
    actionTypes.some((candidate) => candidate === value)
  )
}

const isActionEvidenceValue = (
  value: unknown,
): value is ActionRow['evidence'] => {
  return (
    typeof value === 'string' &&
    actionEvidenceValues.some((candidate) => candidate === value)
  )
}

const isActionCrossTypeValue = (
  value: unknown,
): value is NonNullable<ActionRow['crossType']> => {
  return (
    typeof value === 'string' &&
    actionCrossTypeValues.some((candidate) => candidate === value)
  )
}

const isUpdatedActionMutableRowObject = (
  value: unknown,
): value is UpdatedActionMutableRowObject => {
  return typeof value === 'object' && value !== null
}

const normalizeSqliteBooleanValue = (params: {
  readonly value: unknown
  readonly fieldName: 'locked' | 'generatedTx'
}): boolean => {
  const { value, fieldName } = params

  if (value === true || value === 1) {
    return true
  }
  if (value === false || value === 0) {
    return false
  }

  throw new Error(`locked action row ${fieldName} must be boolean`)
}

/**
 * locked action row の任意 string 列を正規化する。
 * @param params 値と列名を受け取る。
 * @returns string または null を返す。
 */
const normalizeOptionalLockedActionStringValue = (params: {
  readonly value: unknown
  readonly fieldName: 'totalPl' | 'totalPlSortKey' | 'app' | 'targetId'
}): string | null => {
  const { value, fieldName } = params

  if (value === null || value === undefined) {
    return null
  }
  if (typeof value === 'string') {
    return value
  }

  throw new Error(`locked action row ${fieldName} must be string or null`)
}

/**
 * locked action row の任意 boolean 列を正規化する。
 * @param params 値と列名を受け取る。
 * @returns boolean または null を返す。
 */
const normalizeOptionalLockedActionBooleanValue = (params: {
  readonly value: unknown
  readonly fieldName: 'hasUnknownPriceTx' | 'hasInsufficientAmountTx'
}): boolean | null => {
  const { value, fieldName } = params

  if (value === null || value === undefined) {
    return null
  }
  if (typeof value === 'boolean') {
    return value
  }

  throw new Error(`locked action row ${fieldName} must be boolean or null`)
}

/**
 * locked action row の任意 number 列を正規化する。
 * @param params 値と列名を受け取る。
 * @returns number または null を返す。
 */
const normalizeOptionalLockedActionNumberValue = (params: {
  readonly value: unknown
  readonly fieldName: 'csvId' | 'apiId'
}): number | null => {
  const { value, fieldName } = params

  if (value === null || value === undefined) {
    return null
  }
  if (typeof value === 'number') {
    return value
  }

  throw new Error(`locked action row ${fieldName} must be number or null`)
}

/**
 * locked action row の不変列を ActionRow に再利用できる型へ正規化する。
 * @param params temporary action として挿入した元 row を受け取る。
 * @returns ActionRow の不変列を返す。
 */
const normalizeLockedActionInvariantValues = (params: {
  readonly row: SQLiteInsertValue<typeof actionTable>
}): LockedActionInvariantValues => {
  const { row } = params

  return {
    totalPl: normalizeOptionalLockedActionStringValue({
      value: row.totalPl,
      fieldName: 'totalPl',
    }),
    totalPlSortKey: normalizeOptionalLockedActionStringValue({
      value: row.totalPlSortKey,
      fieldName: 'totalPlSortKey',
    }),
    app: normalizeOptionalLockedActionStringValue({
      value: row.app,
      fieldName: 'app',
    }),
    targetId: normalizeOptionalLockedActionStringValue({
      value: row.targetId,
      fieldName: 'targetId',
    }),
    hasUnknownPriceTx: normalizeOptionalLockedActionBooleanValue({
      value: row.hasUnknownPriceTx,
      fieldName: 'hasUnknownPriceTx',
    }),
    hasInsufficientAmountTx: normalizeOptionalLockedActionBooleanValue({
      value: row.hasInsufficientAmountTx,
      fieldName: 'hasInsufficientAmountTx',
    }),
    csvId: normalizeOptionalLockedActionNumberValue({
      value: row.csvId,
      fieldName: 'csvId',
    }),
    apiId: normalizeOptionalLockedActionNumberValue({
      value: row.apiId,
      fieldName: 'apiId',
    }),
  }
}

const readUpdatedActionMutableRowField = (params: {
  readonly row: UpdatedActionMutableRowObject
  readonly primaryKey:
    | 'type'
    | 'subType'
    | 'evidence'
    | 'comment'
    | 'loanId'
    | 'crossId'
    | 'crossType'
    | 'locked'
    | 'generatedTx'
    | 'relatedAssetIds'
    | 'fromAddresses'
    | 'toAddresses'
  readonly index: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11
}): unknown => {
  const { row, primaryKey, index } = params
  const indexKey = index.toString()

  if (primaryKey in row) {
    return row[primaryKey]
  }
  if (indexKey in row) {
    return row[indexKey]
  }
  return undefined
}

const normalizeUpdatedActionMutableRow = (params: {
  readonly row: unknown
}): UpdatedActionMutableRow => {
  const { row } = params
  let type: unknown
  let subType: unknown
  let evidence: unknown
  let comment: unknown
  let loanId: unknown
  let crossId: unknown
  let crossType: unknown
  let locked: unknown
  let generatedTx: unknown
  let relatedAssetIds: unknown
  let fromAddresses: unknown
  let toAddresses: unknown

  if (Array.isArray(row)) {
    type = row[0]
    subType = row[1]
    evidence = row[2]
    comment = row[3]
    loanId = row[4]
    crossId = row[5]
    crossType = row[6]
    locked = row[7]
    generatedTx = row[8]
    relatedAssetIds = row[9]
    fromAddresses = row[10]
    toAddresses = row[11]
  } else if (isUpdatedActionMutableRowObject(row)) {
    type = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'type',
      index: 0,
    })
    subType = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'subType',
      index: 1,
    })
    evidence = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'evidence',
      index: 2,
    })
    comment = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'comment',
      index: 3,
    })
    loanId = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'loanId',
      index: 4,
    })
    crossId = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'crossId',
      index: 5,
    })
    crossType = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'crossType',
      index: 6,
    })
    locked = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'locked',
      index: 7,
    })
    generatedTx = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'generatedTx',
      index: 8,
    })
    relatedAssetIds = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'relatedAssetIds',
      index: 9,
    })
    fromAddresses = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'fromAddresses',
      index: 10,
    })
    toAddresses = readUpdatedActionMutableRowField({
      row,
      primaryKey: 'toAddresses',
      index: 11,
    })
  } else {
    throw new Error('locked action row shape must be array or object')
  }

  if (!isActionTypeValue(type)) {
    throw new Error('locked action row type must be valid')
  }
  if (subType !== null && typeof subType !== 'number') {
    throw new Error('locked action row subType must be number or null')
  }
  if (!isActionEvidenceValue(evidence)) {
    throw new Error('locked action row evidence must be valid')
  }
  if (comment !== null && typeof comment !== 'string') {
    throw new Error('locked action row comment must be string or null')
  }
  if (loanId !== null && typeof loanId !== 'string') {
    throw new Error('locked action row loanId must be string or null')
  }
  if (crossId !== null && typeof crossId !== 'string') {
    throw new Error('locked action row crossId must be string or null')
  }
  if (crossType !== null && !isActionCrossTypeValue(crossType)) {
    throw new Error('locked action row crossType must be valid or null')
  }
  if (relatedAssetIds !== null && typeof relatedAssetIds !== 'string') {
    throw new Error('locked action row relatedAssetIds must be string or null')
  }
  if (fromAddresses !== null && typeof fromAddresses !== 'string') {
    throw new Error('locked action row fromAddresses must be string or null')
  }
  if (toAddresses !== null && typeof toAddresses !== 'string') {
    throw new Error('locked action row toAddresses must be string or null')
  }

  return {
    type,
    subType,
    evidence,
    comment,
    loanId,
    crossId,
    crossType,
    locked: normalizeSqliteBooleanValue({ value: locked, fieldName: 'locked' }),
    generatedTx: normalizeSqliteBooleanValue({
      value: generatedTx,
      fieldName: 'generatedTx',
    }),
    relatedAssetIds,
    fromAddresses,
    toAddresses,
  }
}

/**
 * 指定した会計期 id の終了日時を取得する。
 * @param params database と会計期 id を受け取る。
 * @returns 指定会計期の境界情報を返す。
 */
export const getAccountingPeriodBoundary = async (params: {
  readonly database: DatabaseWithTransaction
  readonly accountingPeriodId: number
}): Promise<ClosedAccountingPeriodBoundary> => {
  const { database: databaseWithTransaction, accountingPeriodId } = params
  const accountingPeriodRows = await databaseWithTransaction.database
    .select({ end: accountingPeriodTable.end })
    .from(accountingPeriodTable)
    .where(eq(accountingPeriodTable.id, accountingPeriodId))
    .limit(1)

  if (accountingPeriodRows.length === 0) {
    throw new Error('closed accounting period is not found')
  }

  return {
    accountingPeriodId,
    endTimestamp: accountingPeriodRows[0].end.getTime(),
  }
}

/**
 * portfolio に設定された確定済会計期の終了日時を取得する。
 * @param params database を受け取る。
 * @returns 確定済会計期が未設定なら undefined、設定済みなら期末タイムスタンプを返す。
 */
export const getClosedAccountingPeriodBoundary = async (params: {
  readonly database: DatabaseWithTransaction
}): Promise<ClosedAccountingPeriodBoundary | undefined> => {
  const { database: databaseWithTransaction } = params
  const portfolioRows = await databaseWithTransaction.database
    .select({
      closedAccountingPeriodId: portfolioTable.closedAccountingPeriodId,
    })
    .from(portfolioTable)
    .limit(1)

  if (portfolioRows.length === 0) {
    return undefined
  }

  const closedAccountingPeriodId = portfolioRows[0].closedAccountingPeriodId
  if (closedAccountingPeriodId === null) {
    return undefined
  }

  return getAccountingPeriodBoundary({
    database: databaseWithTransaction,
    accountingPeriodId: closedAccountingPeriodId,
  })
}

/**
 * action 一覧を確定済会計期の内外で分割する。
 * @param params action 一覧と確定済会計期の終了日時を受け取る。
 * @returns 通常 action とロック期間中取引へ振り分けた結果を返す。
 */
export const partitionActionsByFiscalLock = <
  TAction extends { readonly timestamp: number },
>(params: {
  readonly actions: readonly TAction[]
  readonly closedBoundary?: ClosedAccountingPeriodBoundary
}): PartitionedActions<TAction> => {
  const { actions, closedBoundary } = params

  if (closedBoundary === undefined) {
    return {
      openActions: actions,
      lockedActions: [],
    }
  }

  const openActions: TAction[] = []
  const lockedActions: TAction[] = []
  for (const action of actions) {
    if (action.timestamp <= closedBoundary.endTimestamp) {
      lockedActions.push(action)
      continue
    }
    openActions.push(action)
  }

  return {
    openActions,
    lockedActions,
  }
}

/**
 * source id 一覧を確定済会計期の内外で分割する。
 * 時刻を復元できない source は既存データ掃除漏れを避けるため unclassified として返す。
 * @param params source id と対応する時刻一覧、確定済会計期の終了日時を受け取る。
 * @returns open / locked / unclassified の source id 一覧を返す。
 */
export const partitionSourceIdsByFiscalLock = (params: {
  readonly sourceRows: readonly {
    readonly sourceId: string
    readonly timestamp?: number
  }[]
  readonly closedBoundary?: ClosedAccountingPeriodBoundary
}): PartitionedSourceIds => {
  const { sourceRows, closedBoundary } = params
  const openSourceIds = new Set<string>()
  const lockedSourceIds = new Set<string>()
  const unclassifiedSourceIds = new Set<string>()

  for (const sourceRow of sourceRows) {
    if (closedBoundary === undefined) {
      openSourceIds.add(sourceRow.sourceId)
      continue
    }
    if (sourceRow.timestamp === undefined) {
      unclassifiedSourceIds.add(sourceRow.sourceId)
      continue
    }
    if (sourceRow.timestamp <= closedBoundary.endTimestamp) {
      lockedSourceIds.add(sourceRow.sourceId)
      continue
    }
    openSourceIds.add(sourceRow.sourceId)
  }

  return {
    openSourceIds: [...openSourceIds],
    lockedSourceIds: [...lockedSourceIds],
    unclassifiedSourceIds: [...unclassifiedSourceIds],
  }
}

/**
 * action id に紐づく account relation を対象 action のみに絞り込む。
 * @param params relation 一覧と対象 action id 一覧を受け取る。
 * @returns 対象 action の relation 一覧を返す。
 */
export const filterActionAccountRelations = (params: {
  readonly actionAccountRelations: readonly ActionAccountRelationRow[]
  readonly actionIds: readonly string[]
}): readonly ActionAccountRelationRow[] => {
  const { actionAccountRelations, actionIds } = params
  const actionIdSet = new Set(actionIds)

  return actionAccountRelations.filter((row) => actionIdSet.has(row.actionId))
}

/**
 * 単一 account に紐づく actionAccountRelation row 一覧を action から構築する。
 * @param params action 一覧と account id を受け取る。
 * @returns actionAccountRelation row 一覧を返す。
 */
export const createActionAccountRelationRowsForAccount = <
  TAction extends ActionLike,
>(params: {
  readonly actions: readonly TAction[]
  readonly accountId: number
}): readonly ActionAccountRelationRow[] => {
  const { actions, accountId } = params

  return createActionIdList({ actions }).map((actionId) => ({
    actionId,
    accountId,
  }))
}

/**
 * action 一覧から action id 一覧を構築する。
 * @param params action 一覧を受け取る。
 * @returns action id 一覧を返す。
 */
export const createActionIdList = (params: {
  readonly actions: readonly ActionLike[]
}): readonly string[] => {
  const { actions } = params
  return actions.map((action) => `${action.source}_${action.order}`)
}

/**
 * ロック期間中取引 table 向けの insert row を action 一覧から構築する。
 * @param params action 一覧と account relation 一覧を受け取る。
 * @returns lockedPeriodAction 系 table へ保存する row 一覧を返す。
 */
export const createLockedPeriodActionInsertRows = (params: {
  readonly actions: readonly ActionLike[]
  readonly actionAccountRelations: readonly ActionAccountRelationRow[]
}): LockedPeriodActionInsertRows => {
  const { actions, actionAccountRelations } = params
  const { actionSqls, actionTransferSqls, cryptoSqls, nftSqls } =
    actionsToInsertSqls(actions)
  const actionIds = new Set(createActionIdList({ actions }))

  return {
    actionRows: actionSqls.map((row) => ({
      id: row.id,
      sourceId: row.sourceId,
      order: row.order,
      type: row.type,
      subType: row.subType,
      evidence: row.evidence,
      timestamp: row.timestamp,
      comment: row.comment,
      app: row.app,
      loanId: row.loanId,
      crossId: row.crossId,
      crossType: row.crossType,
      targetId: row.targetId,
      locked: row.locked,
      generatedTx: row.generatedTx,
      relatedAssetIds: row.relatedAssetIds,
      fromAddresses: row.fromAddresses,
      toAddresses: row.toAddresses,
      hasUnknownPriceTx: row.hasUnknownPriceTx,
      hasInsufficientAmountTx: row.hasInsufficientAmountTx,
      csvId: row.csvId,
      apiId: row.apiId,
    })),
    actionTransferRows: actionTransferSqls.map((row) => ({
      actionId: row.actionId,
      order: row.order,
      direction: row.direction,
      from: row.from,
      to: row.to,
      cryptoId: row.cryptoId,
      fiatId: row.fiatId,
      nftId: row.nftId,
      amount: row.amount,
      price: row.price,
    })),
    actionAccountRelationRows: actionAccountRelations
      .filter((row) => actionIds.has(row.actionId))
      .map((row) => ({
        actionId: row.actionId,
        accountId: row.accountId,
      })),
    cryptoRows: cryptoSqls,
    nftRows: nftSqls,
  }
}

/**
 * 通常 actionTable 向け insert row を action 一覧から構築する。
 * @param params action 一覧を受け取る。
 * @returns action / transfer / asset の insert row 一覧を返す。
 */
export const createActionInsertRows = (params: {
  readonly actions: readonly ActionLike[]
}): ActionInsertRows => {
  const { actions } = params
  const { actionSqls, actionTransferSqls, cryptoSqls, nftSqls } =
    actionsToInsertSqls(actions)

  return {
    actionRows: actionSqls,
    actionTransferRows: actionTransferSqls,
    cryptoRows: cryptoSqls,
    nftRows: nftSqls,
  }
}

/**
 * 確定済会計期を考慮した open / locked 向け insert row をまとめて構築する。
 * @param params database、action 一覧、locked action 用 relation builder を受け取る。
 * @returns open / locked の action 群と insert row 一覧を返す。
 */
export const prepareFiscalLockInsertRows = async <
  TAction extends ActionLike & { readonly timestamp: number },
>(params: {
  readonly database: DatabaseWithTransaction
  readonly actions: readonly TAction[]
  readonly createLockedActionAccountRelations: (params: {
    readonly lockedActions: readonly TAction[]
  }) => readonly ActionAccountRelationRow[]
}): Promise<PreparedFiscalLockInsertRows<TAction>> => {
  const { database, actions, createLockedActionAccountRelations } = params
  const closedBoundary = await getClosedAccountingPeriodBoundary({ database })
  const { openActions, lockedActions } = partitionActionsByFiscalLock({
    actions,
    closedBoundary,
  })
  const insertableLockedActions = await filterInsertableLockedActions({
    database: database.database,
    lockedActions,
  })

  return {
    openActions,
    lockedActions,
    openActionIds: createActionIdList({ actions: openActions }),
    openInsertRows: createActionInsertRows({ actions: openActions }),
    lockedInsertRows: createLockedPeriodActionInsertRows({
      actions: insertableLockedActions,
      actionAccountRelations: createLockedActionAccountRelations({
        lockedActions: insertableLockedActions,
      }),
    }),
  }
}

/**
 * 通常 action と重複する locked action を除外する。
 * @param params database と locked action 一覧を受け取る。
 * @returns 通常 action に未登録の locked action 一覧を返す。
 */
const filterInsertableLockedActions = async <
  TAction extends ActionLike & { readonly timestamp: number },
>(params: {
  readonly database: DatabaseWithTransaction['database']
  readonly lockedActions: readonly TAction[]
}): Promise<readonly TAction[]> => {
  const { database, lockedActions } = params
  if (lockedActions.length === 0) {
    return lockedActions
  }

  const existingActionIds = new Set(
    await selectExistingActionIds({
      database,
      actionIds: createActionIdList({ actions: lockedActions }),
    }),
  )
  if (existingActionIds.size === 0) {
    return lockedActions
  }

  return lockedActions.filter(
    (action) => !existingActionIds.has(`${action.source}_${action.order}`),
  )
}

/**
 * open / locked 共通の asset insert query 群を構築する。
 * @param params transaction と insert row 一覧を受け取る。
 * @returns crypto / nft 保存 query 一覧を返す。
 */
export const createAssetInsertQueries = (params: {
  readonly tx: TransactionDatabase
  readonly openInsertRows: ActionInsertRows
  readonly lockedInsertRows: LockedPeriodActionInsertRows
}): Promise<unknown>[] => {
  const { tx, openInsertRows, lockedInsertRows } = params

  return [
    ...divideArrayIntoChunks(
      [...openInsertRows.cryptoRows, ...lockedInsertRows.cryptoRows],
      getMaxInsertRowCount(cryptoCurrencyColumnCount),
    ).map((chunk) =>
      tx
        .insert(cryptoCurrencyTable)
        .values([...chunk])
        .onConflictDoNothing(),
    ),
    ...divideArrayIntoChunks(
      [...openInsertRows.nftRows, ...lockedInsertRows.nftRows],
      getMaxInsertRowCount(nftColumnCount),
    ).map((chunk) =>
      tx
        .insert(nftTable)
        .values([...chunk])
        .onConflictDoNothing(),
    ),
  ]
}

/**
 * 行一覧を chunk ごとに順番に insert する。
 * @param params 行一覧、chunk size、実際の insert 処理を受け取る。
 */
const insertRowsInChunks = async <TRow>(params: {
  readonly rows: readonly TRow[]
  readonly chunkSize: number
  readonly insertChunk: (chunk: readonly TRow[]) => unknown
}): Promise<void> => {
  const { rows, chunkSize, insertChunk } = params

  for (const chunk of divideArrayIntoChunks(rows, chunkSize)) {
    await insertChunk(chunk)
  }
}

/**
 * 通常 action 系 table 向け insert query 群を構築する。
 * @param params transaction、open action の insert row、account relation row を受け取る。
 * @returns 通常 action 保存 query 一覧を返す。
 */
export const createOpenActionInsertQueries = (params: {
  readonly tx: TransactionDatabase
  readonly openInsertRows: ActionInsertRows
  readonly actionAccountRelationRows: readonly SQLiteInsertValue<
    typeof actionAccountRelationTable
  >[]
}): Promise<unknown>[] => {
  const { tx, openInsertRows, actionAccountRelationRows } = params

  return [
    (async () => {
      await insertRowsInChunks({
        rows: openInsertRows.actionRows,
        chunkSize: Math.min(
          getMaxInsertRowCount(actionColumnCount + 3),
          Math.floor(sqliteMaxCompoundSelectCount / 3),
        ),
        insertChunk: (chunk) => tx.insert(actionTable).values([...chunk]),
      })
      await insertRowsInChunks({
        rows: openInsertRows.actionTransferRows,
        chunkSize: getMaxInsertRowCount(actionTransferColumnCount),
        insertChunk: (chunk) =>
          tx.insert(actionTransferTable).values([...chunk]),
      })
      await insertRowsInChunks({
        rows: actionAccountRelationRows,
        chunkSize: getMaxInsertRowCount(actionAccountRelationColumnCount),
        insertChunk: (chunk) =>
          tx
            .insert(actionAccountRelationTable)
            .values([...chunk])
            .onConflictDoNothing(),
      })
    })(),
  ]
}

/**
 * ロック期間中取引 table 向け insert query 群を構築する。
 * @param params transaction と locked action insert row 一覧を受け取る。
 * @returns lockedPeriodAction 系保存 query 一覧を返す。
 */
export const createLockedActionInsertQueries = (params: {
  readonly tx: TransactionDatabase
  readonly lockedInsertRows: LockedPeriodActionInsertRows
}): Promise<unknown>[] => {
  const { tx, lockedInsertRows } = params

  return [
    (async () => {
      await insertRowsInChunks({
        rows: lockedInsertRows.actionRows,
        chunkSize: getMaxInsertRowCount(actionColumnCount),
        insertChunk: (chunk) =>
          tx.insert(lockedPeriodActionTable).values([...chunk]),
      })
      await insertRowsInChunks({
        rows: lockedInsertRows.actionTransferRows,
        chunkSize: getMaxInsertRowCount(actionTransferColumnCount),
        insertChunk: (chunk) =>
          tx.insert(lockedPeriodActionTransferTable).values([...chunk]),
      })
      await insertRowsInChunks({
        rows: lockedInsertRows.actionAccountRelationRows,
        chunkSize: getMaxInsertRowCount(actionAccountRelationColumnCount),
        insertChunk: (chunk) =>
          tx
            .insert(lockedPeriodActionAccountRelationTable)
            .values([...chunk])
            .onConflictDoNothing(),
      })
    })(),
  ]
}

/**
 * source id 文字列から actionSourceTable 向け insert row を生成する。
 * @param params source id 一覧を受け取る。
 * @returns actionSourceTable 用 insert row 一覧を返す。
 */
export const createActionSourceInsertRows = (params: {
  readonly sourceIds: readonly string[]
}): readonly SQLiteInsertValue<typeof actionSourceTable>[] => {
  const { sourceIds } = params
  return sourceIds.map((sourceId) => createActionSourceInsertRow({ sourceId }))
}

/**
 * ロック期間中取引を通常 action へ戻す insert row を構築する。
 * @param params lockedPeriodAction 系 row 一覧を受け取る。
 * @returns 通常 action 系 table 向け insert row 一覧を返す。
 */
export const createActionRowsFromLockedPeriodActions = (params: {
  readonly actionRows: readonly LockedPeriodActionRow[]
  readonly actionTransferRows: readonly LockedPeriodActionTransferRow[]
  readonly actionAccountRelationRows: readonly LockedPeriodActionAccountRelationRow[]
}): {
  readonly actionRows: readonly SQLiteInsertValue<typeof actionTable>[]
  readonly actionTransferRows: readonly SQLiteInsertValue<
    typeof actionTransferTable
  >[]
  readonly actionAccountRelationRows: readonly SQLiteInsertValue<
    typeof lockedPeriodActionAccountRelationTable
  >[]
} => {
  const { actionRows, actionTransferRows, actionAccountRelationRows } = params

  return {
    actionRows: actionRows.map((row) => ({
      id: row.id,
      sourceId: row.sourceId,
      order: row.order,
      type: row.type,
      subType: row.subType,
      evidence: row.evidence,
      timestamp: row.timestamp,
      comment: row.comment,
      app: row.app,
      loanId: row.loanId,
      crossId: row.crossId,
      crossType: row.crossType,
      targetId: row.targetId,
      locked: row.locked,
      generatedTx: row.generatedTx,
      relatedAssetIds: row.relatedAssetIds,
      fromAddresses: row.fromAddresses,
      toAddresses: row.toAddresses,
      hasUnknownPriceTx: row.hasUnknownPriceTx,
      hasInsufficientAmountTx: row.hasInsufficientAmountTx,
      csvId: row.csvId,
      apiId: row.apiId,
    })),
    actionTransferRows: actionTransferRows.map((row) => ({
      actionId: row.actionId,
      order: row.order,
      direction: row.direction,
      from: row.from,
      to: row.to,
      cryptoId: row.cryptoId,
      fiatId: row.fiatId,
      nftId: row.nftId,
      amount: row.amount,
      price: row.price,
    })),
    actionAccountRelationRows: actionAccountRelationRows.map((row) => ({
      actionId: row.actionId,
      accountId: row.accountId,
    })),
  }
}

/**
 * ロック期間中取引 insert row を、一時的に actionTable へ保存するための insert row に変換する。
 * @param params lockedPeriodAction 系 insert row 一覧を受け取る。
 * @returns actionTable 系 insert row 一覧を返す。
 */
const createActionInsertRowsFromLockedInsertRows = (params: {
  readonly lockedInsertRows: LockedPeriodActionInsertRows
}): {
  readonly openInsertRows: ActionInsertRows
  readonly actionAccountRelationRows: readonly SQLiteInsertValue<
    typeof actionAccountRelationTable
  >[]
} => {
  const { lockedInsertRows } = params

  return {
    openInsertRows: {
      actionRows: lockedInsertRows.actionRows.map((row) => ({
        id: row.id,
        sourceId: row.sourceId,
        order: row.order,
        type: row.type,
        subType: row.subType,
        evidence: row.evidence,
        timestamp: row.timestamp,
        comment: row.comment,
        app: row.app,
        loanId: row.loanId,
        crossId: row.crossId,
        crossType: row.crossType,
        targetId: row.targetId,
        locked: row.locked,
        generatedTx: row.generatedTx,
        relatedAssetIds: row.relatedAssetIds,
        fromAddresses: row.fromAddresses,
        toAddresses: row.toAddresses,
        hasUnknownPriceTx: row.hasUnknownPriceTx,
        hasInsufficientAmountTx: row.hasInsufficientAmountTx,
        csvId: row.csvId,
        apiId: row.apiId,
      })),
      actionTransferRows: lockedInsertRows.actionTransferRows.map((row) => ({
        actionId: row.actionId,
        order: row.order,
        direction: row.direction,
        from: row.from,
        to: row.to,
        cryptoId: row.cryptoId,
        fiatId: row.fiatId,
        nftId: row.nftId,
        amount: row.amount,
        price: row.price,
      })),
      cryptoRows: lockedInsertRows.cryptoRows,
      nftRows: lockedInsertRows.nftRows,
    },
    actionAccountRelationRows: lockedInsertRows.actionAccountRelationRows.map(
      (row) => ({
        actionId: row.actionId,
        accountId: row.accountId,
      }),
    ),
  }
}

/**
 * 一時保存した actionTable 系 row を lockedPeriodAction 系 insert row へ戻す。
 * @param params action / transfer / relation row と asset row を受け取る。
 * @returns lockedPeriodAction 系 insert row 一覧を返す。
 */
const createLockedInsertRowsFromActionRows = (params: {
  readonly actionRows: readonly ActionRow[]
  readonly actionTransferRows: readonly ActionTransferRow[]
  readonly actionAccountRelationRows: readonly ActionAccountRelationRow[]
  readonly cryptoRows: readonly SQLiteInsertValue<typeof cryptoCurrencyTable>[]
  readonly nftRows: readonly SQLiteInsertValue<typeof nftTable>[]
}): LockedPeriodActionInsertRows => {
  const {
    actionRows,
    actionTransferRows,
    actionAccountRelationRows,
    cryptoRows,
    nftRows,
  } = params

  return {
    actionRows: actionRows.map((row) => ({
      id: row.id,
      sourceId: row.sourceId,
      order: row.order,
      type: row.type,
      subType: row.subType,
      evidence: row.evidence,
      timestamp: row.timestamp,
      comment: row.comment,
      app: row.app,
      loanId: row.loanId,
      crossId: row.crossId,
      crossType: row.crossType,
      targetId: row.targetId,
      locked: row.locked,
      generatedTx: row.generatedTx,
      relatedAssetIds: row.relatedAssetIds,
      fromAddresses: row.fromAddresses,
      toAddresses: row.toAddresses,
      hasUnknownPriceTx: row.hasUnknownPriceTx,
      hasInsufficientAmountTx: row.hasInsufficientAmountTx,
      csvId: row.csvId,
      apiId: row.apiId,
    })),
    actionTransferRows: actionTransferRows.map((row) => ({
      actionId: row.actionId,
      order: row.order,
      direction: row.direction,
      from: row.from,
      to: row.to,
      cryptoId: row.cryptoId,
      fiatId: row.fiatId,
      nftId: row.nftId,
      amount: row.amount,
      price: row.price,
    })),
    actionAccountRelationRows: actionAccountRelationRows.map((row) => ({
      actionId: row.actionId,
      accountId: row.accountId,
    })),
    cryptoRows,
    nftRows,
  }
}

/**
 * raw row が object 形式かどうかを判定する。
 * @param value 判定対象の値を受け取る。
 * @returns object 形式なら true を返す。
 */
const isUpdatedActionTransferMutableRowObject = (
  value: unknown,
): value is UpdatedActionTransferMutableRowObject => {
  return typeof value === 'object' && value !== null
}

/**
 * raw object row から指定列を読む。
 * @param params row と primary / fallback key を受け取る。
 * @returns 見つかった列値を返す。見つからなければ undefined を返す。
 */
const readUpdatedActionTransferMutableRowField = (params: {
  readonly row: UpdatedActionTransferMutableRowObject
  readonly primaryKey: 'cryptoId' | 'fiatId' | 'nftId' | 'amount' | 'price'
  readonly fallbackKey?: 'cryptoid' | 'fiatid' | 'nftid'
  readonly index: 0 | 1 | 2 | 3 | 4
}): unknown => {
  const { row, primaryKey, fallbackKey, index } = params
  const indexKey = index.toString()

  if (primaryKey in row) {
    return row[primaryKey]
  }
  if (fallbackKey !== undefined && fallbackKey in row) {
    return row[fallbackKey]
  }
  if (indexKey in row) {
    return row[indexKey]
  }
  return undefined
}

/**
 * raw SQL で取得した transfer の可変列を検証し、browser / Bun の両方で安定して扱える形へ正規化する。
 * @param params raw row を受け取る。
 * @returns cryptoId / fiatId / nftId / amount / price を返す。
 */
const normalizeUpdatedActionTransferMutableRow = (params: {
  readonly row: unknown
}): UpdatedActionTransferMutableRow => {
  const { row } = params
  let cryptoId: unknown
  let fiatId: unknown
  let nftId: unknown
  let amount: unknown
  let price: unknown

  if (Array.isArray(row)) {
    cryptoId = row[0]
    fiatId = row[1]
    nftId = row[2]
    amount = row[3]
    price = row[4]
  } else if (isUpdatedActionTransferMutableRowObject(row)) {
    cryptoId = readUpdatedActionTransferMutableRowField({
      row,
      primaryKey: 'cryptoId',
      fallbackKey: 'cryptoid',
      index: 0,
    })
    fiatId = readUpdatedActionTransferMutableRowField({
      row,
      primaryKey: 'fiatId',
      fallbackKey: 'fiatid',
      index: 1,
    })
    nftId = readUpdatedActionTransferMutableRowField({
      row,
      primaryKey: 'nftId',
      fallbackKey: 'nftid',
      index: 2,
    })
    amount = readUpdatedActionTransferMutableRowField({
      row,
      primaryKey: 'amount',
      index: 3,
    })
    price = readUpdatedActionTransferMutableRowField({
      row,
      primaryKey: 'price',
      index: 4,
    })
  } else {
    throw new Error('locked action transfer row shape must be array or object')
  }

  if (cryptoId !== null && typeof cryptoId !== 'string') {
    throw new Error(
      'locked action transfer row cryptoId must be string or null',
    )
  }
  if (fiatId !== null && typeof fiatId !== 'string') {
    throw new Error('locked action transfer row fiatId must be string or null')
  }
  if (nftId !== null && typeof nftId !== 'string') {
    throw new Error('locked action transfer row nftId must be string or null')
  }
  if (typeof amount !== 'string') {
    throw new Error('locked action transfer row amount must be string')
  }
  if (price !== null && typeof price !== 'string') {
    throw new Error('locked action transfer row price must be string or null')
  }

  return {
    cryptoId,
    fiatId,
    nftId,
    amount,
    price,
  }
}

/**
 * ルール適用後の transfer row を、元の actionId / order を保持したまま取得する。
 * @param params database と、ルール適用前の transfer row 一覧を受け取る。
 * @returns actionId / order を保持した transfer row 一覧を返す。
 */
const selectUpdatedActionTransferRows = async (params: {
  readonly database: TransactionDatabase
  readonly originalActionTransferRows: readonly SQLiteInsertValue<
    typeof actionTransferTable
  >[]
}): Promise<readonly ActionTransferRow[]> => {
  const { database, originalActionTransferRows } = params
  const updatedRows: ActionTransferRow[] = []
  for (const originalRow of originalActionTransferRows) {
    if (typeof originalRow.actionId !== 'string') {
      throw new Error('locked action transfer row actionId must be string')
    }
    if (typeof originalRow.order !== 'number') {
      throw new Error('locked action transfer row order must be number')
    }
    if (
      originalRow.direction !== 'in' &&
      originalRow.direction !== 'out' &&
      originalRow.direction !== 'none'
    ) {
      throw new Error('locked action transfer row direction must be valid')
    }
    if (
      originalRow.from !== undefined &&
      originalRow.from !== null &&
      typeof originalRow.from !== 'string'
    ) {
      throw new Error('locked action transfer row from must be string or null')
    }
    if (
      originalRow.to !== undefined &&
      originalRow.to !== null &&
      typeof originalRow.to !== 'string'
    ) {
      throw new Error('locked action transfer row to must be string or null')
    }

    const direction = originalRow.direction
    const from = originalRow.from ?? null
    const to = originalRow.to ?? null

    const rows = await database.values(sql`
      SELECT
        ${actionTransferTable.cryptoId} AS "cryptoId",
        ${actionTransferTable.fiatId} AS "fiatId",
        ${actionTransferTable.nftId} AS "nftId",
        ${actionTransferTable.amount} AS "amount",
        ${actionTransferTable.price} AS "price"
      FROM ${actionTransferTable}
      WHERE ${actionTransferTable.actionId} = ${originalRow.actionId}
        AND ${actionTransferTable.order} = ${originalRow.order}
    `)

    if (rows.length !== 1) {
      throw new Error('locked action transfer row is missing unexpectedly')
    }

    const updatedValues = normalizeUpdatedActionTransferMutableRow({
      row: rows[0],
    })
    updatedRows.push({
      actionId: originalRow.actionId,
      order: originalRow.order,
      direction,
      from,
      to,
      ...updatedValues,
    })
  }

  return updatedRows
}

/**
 * ルール適用後の action row を、元の actionId を保持したまま取得する。
 * @param params database と、ルール適用前の action row 一覧を受け取る。
 * @returns rule 適用後の action row 一覧を返す。
 */
const selectUpdatedActionRows = async (params: {
  readonly database: TransactionDatabase
  readonly originalActionRows: readonly SQLiteInsertValue<typeof actionTable>[]
}): Promise<readonly ActionRow[]> => {
  const { database, originalActionRows } = params
  const updatedRows: ActionRow[] = []

  for (const originalRow of originalActionRows) {
    if (typeof originalRow.id !== 'string') {
      throw new Error('locked action row id must be string')
    }
    if (typeof originalRow.sourceId !== 'string') {
      throw new Error('locked action row sourceId must be string')
    }
    if (typeof originalRow.order !== 'number') {
      throw new Error('locked action row order must be number')
    }
    if (!(originalRow.timestamp instanceof Date)) {
      throw new Error('locked action row timestamp must be Date')
    }
    const invariantValues = normalizeLockedActionInvariantValues({
      row: originalRow,
    })

    const rows = await database.values(sql`
      SELECT
        ${actionTable.type} AS "type",
        ${actionTable.subType} AS "subType",
        ${actionTable.evidence} AS "evidence",
        ${actionTable.comment} AS "comment",
        ${actionTable.loanId} AS "loanId",
        ${actionTable.crossId} AS "crossId",
        ${actionTable.crossType} AS "crossType",
        ${actionTable.locked} AS "locked",
        ${actionTable.generatedTx} AS "generatedTx",
        ${actionTable.relatedAssetIds} AS "relatedAssetIds",
        ${actionTable.fromAddresses} AS "fromAddresses",
        ${actionTable.toAddresses} AS "toAddresses"
      FROM ${actionTable}
      WHERE ${actionTable.id} = ${originalRow.id}
    `)

    if (rows.length !== 1) {
      throw new Error('locked action row is missing unexpectedly')
    }

    const updatedValues = normalizeUpdatedActionMutableRow({ row: rows[0] })

    updatedRows.push({
      id: originalRow.id,
      sourceId: originalRow.sourceId,
      order: originalRow.order,
      timestamp: originalRow.timestamp,
      ...invariantValues,
      ...updatedValues,
    })
  }

  return updatedRows
}

/**
 * ルール適用前の locked relation row をそのまま再利用できる形へ正規化する。
 * @param params locked relation row 一覧を受け取る。
 * @returns actionId / accountId を持つ relation row 一覧を返す。
 */
const normalizeLockedActionAccountRelationRows = (params: {
  readonly rows: readonly SQLiteInsertValue<
    typeof lockedPeriodActionAccountRelationTable
  >[]
}): readonly ActionAccountRelationRow[] => {
  const { rows } = params

  return rows.map((row) => {
    if (typeof row.actionId !== 'string') {
      throw new Error(
        'locked action account relation row actionId must be string',
      )
    }
    if (typeof row.accountId !== 'number') {
      throw new Error(
        'locked action account relation row accountId must be number',
      )
    }
    return {
      actionId: row.actionId,
      accountId: row.accountId,
    }
  })
}

/**
 * 一時保存した locked action に、通常 action と同じ rule 適用を行ってから locked insert row を返す。
 * @param params transaction 中の database、locked insert row、任意の事前変換 callback を受け取る。
 * @returns rule 適用後の locked insert row を返す。
 */
export const applyRulesToLockedInsertRowsInTransaction = async (params: {
  readonly database: TransactionDatabase
  readonly lockedInsertRows: LockedPeriodActionInsertRows
  readonly beforeApplyActionEditRules?: (params: {
    readonly database: TransactionDatabase
    readonly actionIds: readonly string[]
  }) => Promise<void>
}): Promise<LockedPeriodActionInsertRows> => {
  const { database, lockedInsertRows, beforeApplyActionEditRules } = params
  const actionIds = lockedInsertRows.actionRows
    .map((row) => row.id)
    .filter((actionId): actionId is string => typeof actionId === 'string')
  if (actionIds.length !== lockedInsertRows.actionRows.length) {
    throw new Error('locked action id must be string')
  }
  if (actionIds.length === 0) {
    return lockedInsertRows
  }

  const { openInsertRows, actionAccountRelationRows } =
    createActionInsertRowsFromLockedInsertRows({ lockedInsertRows })

  await Promise.all(
    createOpenActionInsertQueries({
      tx: database,
      openInsertRows,
      actionAccountRelationRows,
    }),
  )

  try {
    if (beforeApplyActionEditRules !== undefined) {
      await beforeApplyActionEditRules({ database, actionIds })
    }

    await applyActionEditRulesToInsertedActionsOnDatabase({
      database,
      actionIdList: actionIds,
      invalidateCalculationState: false,
    })

    const [actionRows, actionTransferRows] = await Promise.all([
      selectUpdatedActionRows({
        database,
        originalActionRows: openInsertRows.actionRows,
      }),
      selectUpdatedActionTransferRows({
        database,
        originalActionTransferRows: openInsertRows.actionTransferRows,
      }),
    ])
    const actionAccountRelationRowsAfterRule =
      normalizeLockedActionAccountRelationRows({
        rows: lockedInsertRows.actionAccountRelationRows,
      })

    await deleteActionRowsByIds({
      database,
      actionIds,
    })

    return createLockedInsertRowsFromActionRows({
      actionRows,
      actionTransferRows,
      actionAccountRelationRows: actionAccountRelationRowsAfterRule,
      cryptoRows: lockedInsertRows.cryptoRows,
      nftRows: lockedInsertRows.nftRows,
    })
  } catch (error) {
    await deleteActionRowsByIds({
      database,
      actionIds,
    })
    throw error
  }
}

/**
 * 一時保存した locked action に rule を適用して locked insert row へ戻す。
 * @param params database、locked insert row、任意の事前変換 callback を受け取る。
 * @returns rule 適用後の locked insert row を返す。
 */
export const applyRulesToLockedInsertRows = async (params: {
  readonly database: DatabaseWithTransaction
  readonly lockedInsertRows: LockedPeriodActionInsertRows
  readonly beforeApplyActionEditRules?: (params: {
    readonly database: TransactionDatabase
    readonly actionIds: readonly string[]
  }) => Promise<void>
}): Promise<LockedPeriodActionInsertRows> => {
  const { database, lockedInsertRows, beforeApplyActionEditRules } = params
  let preparedLockedInsertRows: LockedPeriodActionInsertRows | undefined

  await database.transaction([
    (tx) => [
      (async () => {
        preparedLockedInsertRows =
          await applyRulesToLockedInsertRowsInTransaction({
            database: tx,
            lockedInsertRows,
            beforeApplyActionEditRules,
          })
      })(),
    ],
  ])

  if (preparedLockedInsertRows === undefined) {
    throw new Error('failed to prepare locked insert rows')
  }

  return preparedLockedInsertRows
}

/**
 * 単一 source id から actionSourceTable 向け insert row を生成する。
 * @param params source id を受け取る。
 * @returns actionSourceTable 用 insert row を返す。
 */
const createActionSourceInsertRow = (params: {
  readonly sourceId: string
}): SQLiteInsertValue<typeof actionSourceTable> => {
  const { sourceId } = params

  if (sourceId.startsWith('evm_')) {
    const [, evmChainId, hash] = sourceId.split('_')
    return {
      id: sourceId,
      type: 'evm',
      evmChainId: Number.parseInt(evmChainId, 10),
      evmHash: hexToBlob(hash as Hex),
    }
  }

  if (sourceId.startsWith('polkadot_')) {
    const [, polkadotChainId, hash] = sourceId.split('_')
    const [polkadotBlockNumber, polkadotExtrinsicIndex] = hash.split('-')
    return {
      id: sourceId,
      type: 'polkadot',
      polkadotChainId: Number.parseInt(polkadotChainId, 10),
      polkadotBlockNumber: Number.parseInt(polkadotBlockNumber, 10),
      polkadotExtrinsicIndex:
        polkadotExtrinsicIndex === undefined
          ? undefined
          : Number.parseInt(polkadotExtrinsicIndex, 10),
    }
  }

  if (sourceId.startsWith('solana_')) {
    const [, solanaSignature] = sourceId.split('_')
    return {
      id: sourceId,
      type: 'solana',
      solanaSignature,
    }
  }

  return {
    id: sourceId,
    type: 'other',
  }
}

/**
 * 既に通常 actionTable に存在する action id 一覧を返す。
 * @param params database と action id 一覧を受け取る。
 * @returns 既存 action id 一覧を返す。
 */
const selectExistingActionIds = async (params: {
  readonly database: TransactionDatabase
  readonly actionIds: readonly string[]
}): Promise<readonly string[]> => {
  const { database, actionIds } = params
  if (actionIds.length === 0) {
    return []
  }

  const chunks = await Promise.all(
    divideArrayIntoChunks(actionIds, sqliteMaxHostParameterCount).map((chunk) =>
      database
        .select({ id: actionTable.id })
        .from(actionTable)
        .where(inArray(actionTable.id, [...chunk])),
    ),
  )

  return chunks.flat().map((row) => row.id)
}

/**
 * 指定 action id の action row を削除する。
 * @param params database と action id 一覧を受け取る。
 * @returns 削除完了後に resolve する。
 */
const deleteActionRowsByIds = async (params: {
  readonly database: TransactionDatabase
  readonly actionIds: readonly string[]
}): Promise<void> => {
  const { database, actionIds } = params
  if (actionIds.length === 0) {
    return
  }

  await deleteAssistantProposalsForActionIds({ database, actionIds })
  await Promise.all(
    divideArrayIntoChunks(actionIds, sqliteMaxHostParameterCount).map((chunk) =>
      database.delete(actionTable).where(inArray(actionTable.id, [...chunk])),
    ),
  )
}
